/**
 * OperatorDashboard
 * Main entry point for the EV Operator Dashboard feature
 * Provides tab navigation between dashboard pages
 */

import { useState, useEffect } from 'react';
import { Activity, BarChart3, AlertTriangle, Users } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/core/components/ui/tabs';
import { FleetStatusPage } from './components/pages/FleetStatusPage';
import { AnalyticsPage } from './components/pages/AnalyticsPage';
import { AlertsPage } from './components/pages/AlertsPage';
import { AccountsPage } from './components/pages/AccountsPage';
import { cn } from '@/core/lib/utils';
import { useOperatorStore } from '@/core/stores/operator-store';
import { useAuthStore } from '@/core/stores/auth-store';
import { ActorSelector } from '@/core/components/ActorSelector';
import { LocationSelector } from '@/core/components/LocationSelector';
import { appConfig } from '@/core/config';

type TabValue = 'fleet' | 'analytics' | 'alerts' | 'accounts';

interface TabConfig {
  value: TabValue;
  label: string;
  icon: React.ReactNode;
}

const TABS: TabConfig[] = [
  { value: 'fleet', label: 'Live Status', icon: <Activity className="h-4 w-4" /> },
  { value: 'analytics', label: 'Analytics', icon: <BarChart3 className="h-4 w-4" /> },
  // { value: 'sessions', label: 'Sessions', icon: <Zap className="h-4 w-4" /> },
  { value: 'accounts', label: 'Accounts', icon: <Users className="h-4 w-4" /> },
  { value: 'alerts', label: 'Alerts', icon: <AlertTriangle className="h-4 w-4" /> },
];

export function OperatorDashboard() {
  const [activeTab, setActiveTab] = useState<TabValue>('fleet');
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const { selectedOperator, operators, isLoading, selectOperator, loadOperators } = useOperatorStore();
  const { isDistributor } = useAuthStore();

  // Filter tabs based on runtime environment (hide Alerts in production only, hide Accounts in all environments)
  const runtimeConfig = appConfig.getRuntimeConfig();
  const isProductionEnv = runtimeConfig?.environment === 'prod';

  const visibleTabs = TABS.filter(tab => {
    if (tab.value === 'alerts' && isProductionEnv) {
      return false;
    }
    if (tab.value === 'accounts') {
      return false; // Hide Accounts tab in all environments
    }
    return true;
  });

  // Load operators on mount for distributors
  useEffect(() => {
    if (isDistributor && operators.length === 0 && !isLoading) {
      loadOperators();
    }
  }, [isDistributor, operators.length, isLoading, loadOperators]);

  const handleOperatorChange = (operatorId: string) => {
    const selectedOp = operators.find(op => op.operatorId.toString() === operatorId);
    if (selectedOp) {
      selectOperator(selectedOp);
      // Reset location filter when operator changes
      setSelectedLocationId('');
    }
  };

  const handleLocationChange = (locationId: string) => {
    setSelectedLocationId(locationId);
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gray-50">
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabValue)} className="flex flex-col flex-1 overflow-hidden">
        {/* Tab Navigation with Filters */}
        <div className="flex-shrink-0 bg-white px-6">
          <div className="flex items-center justify-between h-14">
            <TabsList className="h-14 bg-transparent p-0 gap-1">
              {visibleTabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className={cn(
                    'h-14 px-4 rounded-none border-b-2 border-transparent',
                    'data-[state=active]:border-blue-600 data-[state=active]:bg-transparent',
                    'data-[state=active]:text-blue-600 data-[state=active]:shadow-none',
                    'hover:text-blue-600 transition-colors'
                  )}
                >
                  <span className="flex items-center gap-2">
                    {tab.icon}
                    {tab.label}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Filters on the right */}
            <div className="flex items-center gap-3">
              {/* Location Filter - Hidden */}
              {/* {selectedOperator && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Location:</label>
                  <LocationSelector
                    operatorId={selectedOperator.operatorId}
                    selectedLocationId={selectedLocationId}
                    onLocationChange={handleLocationChange}
                    autoSelectFirst={false}
                    className="w-64"
                  />
                </div>
              )} */}

              {/* Operator Filter - Only for distributors */}
              {isDistributor && operators.length > 0 && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Operator:</label>
                  <ActorSelector
                    operators={operators}
                    selectedOperatorId={selectedOperator?.operatorId.toString() || ''}
                    onOperatorChange={handleOperatorChange}
                    isLoading={isLoading}
                    className="w-64"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <TabsContent value="fleet" className="mt-0 flex-1 overflow-auto">
          <FleetStatusPage />
        </TabsContent>
        <TabsContent value="analytics" className="mt-0 flex-1 overflow-auto">
          <AnalyticsPage />
        </TabsContent>
        {/* <TabsContent value="sessions" className="mt-0 flex-1 overflow-auto">
          <SessionsPage />
        </TabsContent> */}
        {/* Accounts tab hidden in all environments */}
        {!isProductionEnv && (
          <TabsContent value="alerts" className="mt-0 flex-1 overflow-auto">
            <AlertsPage />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

export default OperatorDashboard;
